import { z } from 'zod';

import { makeEndpoint } from '../common';

// Detailed auction timings
// convention: "offset" refers to time prior and "grace period" is time after
export const AUCTION_MIN_DURATION_MS: number = 60000; // 1 minute
export const AUCTION_OPEN_SOON_OFFSET_MINUTES = 5;
export const AUCTION_CLOSE_SOON_OFFSET_MINUTES: number = 5;
export const AUCTION_CLOSE_VERY_SOON_OFFSET_MINUTES: number = 2;
export const AUCTION_HARD_CLOSE_GRACE_PERIOD_MINUTES: number = 1;
export const AUCTION_OFFER_REMIND_GRACE_PERIOD_HOURS = 24;
export const AUCTION_OFFER_LAST_CHANCE_GRACE_PERIOD_HOURS = 48;
export const AUCTION_OFFER_CLAIM_GRACE_PERIOD_HOURS = 72;
export const AUCTION_OPEN_GRACE_PERIOD_MINUTES = 5;
export const AUCTION_LOCKED_PERIOD_MS = 60000; // 1 minute
export const AUCTION_UNPUBLISH_LOCK_MINUTES = 15;

export const AUCTION_STATUS_TOPIC_NAME = 'auction-status';

export const AUCTION_DEBUG_INTERVAL_MINUTES = 2;

// Debug settings - TODO: Move to config/feature flags
export const AUCTION_DEFAULT_MIN_BID_INCREMENT = 0;
export const AUCTION_DEFAULT_START_AMOUNT = 0;

// Lifecycle moments (distinct from AuctionStatus)
export enum AuctionMoment {
  PREVIEW = 'PREVIEW',
  OPEN_SOON = 'OPEN_SOON',
  OPEN = 'OPEN',
  OPEN_END = 'OPEN_END',
  CLOSE_SOON = 'CLOSE_SOON',
  CLOSE_VERY_SOON = 'CLOSE_VERY_SOON',
  CLOSE = 'CLOSE',
  HARD_CLOSE = 'HARD_CLOSE',
  // END is only reached when an offer is accepted, when we run out of bidders to offer to, or when we forcibly end the auction.
  // No timer is created for that moment.
  END = 'END',
}

// Offers are made upon hard close and upon expired offers.
// No timer is created for that moment
export enum OfferMoment {
  REMIND = 'REMIND',
  LAST_CHANCE = 'LAST_CHANCE',
  EXPIRE = 'EXPIRE',
}

export enum AuctionErrorCode {
  // Auction availability errors (AUCTION_UNAVAILABLE_*)
  AUCTION_UNAVAILABLE_NOT_OPEN = 'auction_unavailable.not_open',
  AUCTION_UNAVAILABLE_ENDED = 'auction_unavailable.ended',
  AUCTION_UNAVAILABLE_CANCELLED = 'auction_unavailable.cancelled',
  AUCTION_UNAVAILABLE_CLOSED = 'auction_unavailable.closed',

  // Auction update errors (AUCTION_UPDATE_*)
  AUCTION_UPDATE_INVALID = 'auction_update.invalid',
  AUCTION_UPDATE_CLOSE_DATE_BACKWARD = 'auction_update.close_date_backward',
  AUCTION_UPDATE_TIMER_CONFLICT = 'auction_update.timer_conflict',

  // Bid validation errors (BID_INVALID_*)
  BID_INVALID_BELOW_STARTING = 'bid_invalid.below_starting',
  BID_INVALID_BELOW_INCREMENT = 'bid_invalid.below_increment',
  BID_INVALID_AMOUNT_TYPE = 'bid_invalid.amount_type',
  BID_INVALID_NEGATIVE = 'bid_invalid.negative',
  BID_INVALID_MAX_BID_AMOUNT = 'bid_invalid.max_bid_amount',
  BID_INVALID_BELOW_CURRENT = 'bid_invalid.below_current',
  BID_INVALID_MISSING = 'bid_invalid.missing',
  BID_INVALID_FORMAT = 'bid_invalid.format',
}

export enum AuctionStatus {
  PREVIEW = 'preview',
  OPEN = 'open',
  ENDED = 'ended',
  CLOSED = 'closed',
  CANCELED = 'canceled',
  HARD_CLOSE = 'hard_close',
}

// States that require retry until success
export const AUCTION_RETRY_UNTIL_SUCCESS_MOMENTS = [AuctionMoment.OPEN_END, AuctionMoment.HARD_CLOSE, OfferMoment.EXPIRE];

// States that can be skipped if we're already past their time
export const AUCTION_SKIPPABLE_MOMENTS = [
  AuctionMoment.OPEN_SOON,
  AuctionMoment.CLOSE_SOON,
  AuctionMoment.CLOSE_VERY_SOON,
  AuctionMoment.CLOSE,
  OfferMoment.REMIND,
  OfferMoment.LAST_CHANCE,
];

// States that must succeed or be explicitly cancelled - no automatic progression
export const AUCTION_MUST_SUCCEED_MOMENTS = AUCTION_RETRY_UNTIL_SUCCESS_MOMENTS;

export enum AuctionAmountType {
  POINTS = 'points',
  CASH = 'cash',
}

export enum WinningBidStatus {
  WINNING = 'winning',
  WON = 'won',
}

export enum WinningBidType {
  STANDARD = 'standard',
  SECOND_CHANCE = 'secondChance',
}

export enum BidStatus {
  PLACED = 'placed',
  OFFERED = 'offered',
  OFFER_EXPIRED = 'offer_expired',
  CONFIRMED = 'confirmed',
  REJECTED = 'rejected',
}

export type AuctionUpdate = {
  readonly uniqueBiddersCount: number;
  readonly bidsCount: number;
  readonly id: string;
  readonly status: AuctionStatus;
  readonly winningAmount: string | null;
  readonly winningBidderId: string | null;
};

// Base schema for internal use
export const AuctionSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  resourceGroupCollectionId: z.string().uuid(),
  status: z.nativeEnum(AuctionStatus),
  amountType: z.nativeEnum(AuctionAmountType).optional(),
  startingAmount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' })
    .optional(),
  minBidIncrement: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' })
    .optional(),
  biddingOpenDateTime: z.union([z.date().transform((date) => date.toISOString()), z.string().datetime()]),
  biddingCloseDateTime: z.union([z.date().transform((date) => date.toISOString()), z.string().datetime()]),
  claimTimeMinutes: z.number().optional(),
  version: z.string().optional(),
  winningAmount: z.string().optional().nullable(),
  winningBidderId: z.string().optional().nullable(),
});

// Type for the internal schema
export type Auction = z.infer<typeof AuctionSchema>;

// Schema for API responses
export const AuctionResponse = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  resourceGroupCollectionId: z.string().uuid(),
  status: z.nativeEnum(AuctionStatus),
  startingAmount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  minBidIncrement: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  biddingOpenDateTime: z.union([z.date().transform((date) => date.toISOString()), z.string().datetime()]),
  biddingCloseDateTime: z.union([z.date().transform((date) => date.toISOString()), z.string().datetime()]),
  // Display timezone (IANA) for the bidding open/close times — see AuctionResponseSchema (WAY-14834).
  biddingTimezoneId: z.string().optional(),
  claimTimeMinutes: z.number(),
});

export const EnrichedAuctionResponseSchema = AuctionSchema.extend({
  bidsCount: z.number(),
  uniqueBiddersCount: z.number(),
  publishedStatus: z.string(),
  publishedId: z.string().uuid(),
  createdAt: z.date().transform((date) => date.toISOString()),
  updatedAt: z.date().transform((date) => date.toISOString()),
});

export const AuctionApiResponseSchema = AuctionSchema.extend({
  publishedStatus: z.nativeEnum(AuctionStatus).nullable().optional(),
}).transform((auction) => ({
  ...auction,
  claimTimeMinutes: auction.claimTimeMinutes,
}));

export const BidderInfoSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  email: z.string().email(),
  externalId: z.string(),
  firstName: z.string(),
  lastName: z.string().optional(),
  pointsBalance: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  phoneNumber: z.string().optional(),
  loyaltyTier: z.string().optional(),
  loyaltyNumber: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const BidResponse = z.object({
  id: z.string().uuid(),
  amountType: z.string(),
  amount: z.string().regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' }),
  createdAt: z.date(),
  bidder: BidderInfoSchema,
});

export const GetBidsResponse = z.object({
  items: z.array(BidResponse),
  meta: z.object({
    totalItems: z.number(),
    itemCount: z.number(),
    itemsPerPage: z.number(),
    totalPages: z.number(),
    currentPage: z.number(),
  }),
});

// For PUT operations, all fields are required
export const UpdateAuctionRequest = z.object({
  status: z.nativeEnum(AuctionStatus).optional(),
  startingAmount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' })
    .optional(),
  minBidIncrement: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' })
    .optional(),
  biddingOpenDateTime: z
    .string()
    .transform((v) => new Date(v))
    .optional(),
  biddingCloseDateTime: z
    .string()
    .transform((v) => new Date(v))
    .optional(),
  claimTimeMinutes: z.number().optional(),
});

export const UpdateAuctionEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
  }) => `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}`,
  requestSchema: UpdateAuctionRequest,
  responseSchema: AuctionResponse,
});

// For POST operations
export const CreateAuctionRequest = z.object({
  startingAmount: z.string().regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' }),
  minBidIncrement: z.string().regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number with 2 decimal places.' }),
  biddingOpenDateTime: z.string().transform((v) => new Date(v)),
  biddingCloseDateTime: z.string().transform((v) => new Date(v)),
  claimTimeMinutes: z.number(),
});

export const CreateAuctionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions`,
  requestSchema: CreateAuctionRequest,
  responseSchema: AuctionResponse,
});

// Bid request schema
export const PlaceBidRequestSchema = z.object({
  brandId: z.string().uuid(),
  id: z.string().uuid().optional(),
  amount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  maxAmount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  amountType: z.nativeEnum(AuctionAmountType).optional(),
});

export type PlaceBidRequest = z.infer<typeof PlaceBidRequestSchema>;

export const PlaceBidEndpoint = makeEndpoint({
  method: 'POST',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
  }) => `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/bids`,
  requestSchema: PlaceBidRequestSchema,
  responseSchema: z.void(),
});

export const BidderSchema = z.object({
  brandId: z.string().uuid(),
  id: z.string().uuid(),
  externalId: z.string(),
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional().nullable(),
  pointsBalance: z
    .string()
    .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
    .optional(),
  phoneNumber: z.string().optional(),
  loyaltyTier: z.string().optional(),
  loyaltyNumber: z.string().optional(),
  maxBids: z
    .array(
      z.object({
        auctionId: z.string().uuid(),
        amount: z.string(),
        createdAt: z.date(),
      }),
    )
    .optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const SanitizedBidderInfoSchema = z.object({
  brandId: z.string().uuid(),
  loyaltyTier: z.string().optional(),
  externalIdLast4: z.string().optional(),
});

export const SanitizedBidWithBidderInfoSchema = z.object({
  id: z.string().uuid(),
  amountType: z.string(),
  amount: z
    .string()
    .regex(/^\d+(\.\d{2})?$/)
    .optional(),
  createdAt: z.date(),
  bidder: SanitizedBidderInfoSchema,
});

// Response schema for public use - includes sanitized PII
export const GetSanitizedBidsResponse = z.object({
  items: z.array(SanitizedBidWithBidderInfoSchema).readonly(),
  meta: z.object({
    totalItems: z.number().optional(),
    itemCount: z.number().optional(),
    itemsPerPage: z.number().optional(),
    totalPages: z.number().optional(),
    currentPage: z.number().optional(),
  }),
});

export const GetBidsByBidderResponse = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      amountType: z.string(),
      amount: z
        .string()
        .regex(/^\d+(\.\d{2})?$/, { message: 'Must be a valid decimal number.' })
        .optional(),
      createdAt: z.date(),
      bidder: BidderSchema,
    }),
  ),
  meta: z.object({
    totalItems: z.number().optional(),
    itemCount: z.number().optional(),
    itemsPerPage: z.number().optional(),
    totalPages: z.number().optional(),
    currentPage: z.number().optional(),
  }),
});

export const GetBidsByBidderEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
    field,
    value,
    page,
    limit,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
    field: string;
    value: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/bids/bidder/${field}/${value}${queryString}`;
  },
  requestSchema: z.object({
    field: z.string(),
    value: z.string(),
    page: z.number().optional(),
    limit: z.number().optional(),
  }),
  responseSchema: GetBidsByBidderResponse,
});

export const GetAuctionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
  }),
  responseSchema: EnrichedAuctionResponseSchema,
});

export const GetBidsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
    page,
    limit,
    search,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const queryParams = new URLSearchParams();

    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());
    if (search !== undefined) queryParams.append('q', search);

    const isSearch = search !== undefined;
    const basePath = `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/bids`;
    const path = isSearch ? `${basePath}/search` : basePath;

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return `${path}${queryString}`;
  },
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    auctionId: z.string().uuid(),
    page: z.number().optional(),
    limit: z.number().optional(),
    search: z.string().optional(),
  }),
  responseSchema: GetSanitizedBidsResponse,
});

// Internal endpoint that returns full PII for admin users
export const GetInternalBidsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
    page,
    limit,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return `internal/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/bids${queryString}`;
  },
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    auctionId: z.string().uuid(),
    page: z.number().optional(),
    limit: z.number().optional(),
  }),
  responseSchema: GetBidsResponse,
});

// Get specific auction by ID endpoint
export const GetAuctionByIdEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
  }) => `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    auctionId: z.string().uuid(),
  }),
  responseSchema: AuctionResponse,
});

// Cancel auction endpoint
export const CancelAuctionEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
  }) => `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/cancel`,
  requestSchema: z.void(),
  responseSchema: AuctionResponse,
});

// Get bidder by ID endpoint
export const GetBidderEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, auctionId, bidderId }: { brandId: string; auctionId: string; bidderId: string }) =>
    `v1/brands/${brandId}/auctions/${auctionId}/bidder/${bidderId}`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    auctionId: z.string().uuid(),
    bidderId: z.string().uuid(),
  }),
  responseSchema: BidderSchema,
});

// Public auction endpoint (by resource group collection)
export const GetPublicAuctionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auction`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
  }),
  responseSchema: AuctionResponse,
});

// Public get bids endpoint (sanitized)
export const GetPublicBidsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, auctionId, page, limit }: { brandId: string; auctionId: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return `v1/brands/${brandId}/auctions/${auctionId}/bids${queryString}`;
  },
  requestSchema: z.object({
    brandId: z.string().uuid(),
    auctionId: z.string().uuid(),
    page: z.number().optional(),
    limit: z.number().optional(),
  }),
  responseSchema: GetSanitizedBidsResponse,
});

// Public place bid endpoint
export const PlaceBidPublicEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, auctionId }: { brandId: string; auctionId: string }) => `v1/brands/${brandId}/auctions/${auctionId}/bids`,
  requestSchema: PlaceBidRequestSchema,
  responseSchema: z.void(),
});

// Bidder endpoints
export const GetBiddersEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, page, limit }: { brandId: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return `v1/brands/${brandId}/bidders${queryString}`;
  },
  requestSchema: z.object({
    brandId: z.string().uuid(),
    page: z.number().optional(),
    limit: z.number().optional(),
  }),
  responseSchema: z.object({
    items: z.array(BidderSchema),
    meta: z.object({
      totalItems: z.number().optional(),
      itemCount: z.number().optional(),
      itemsPerPage: z.number().optional(),
      totalPages: z.number().optional(),
      currentPage: z.number().optional(),
    }),
  }),
});

export const GetBidderByIdEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, bidderId }: { brandId: string; bidderId: string }) => `v1/brands/${brandId}/bidders/${bidderId}`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    bidderId: z.string().uuid(),
  }),
  responseSchema: BidderSchema,
});

export const UpdateBidderEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ brandId, bidderId }: { brandId: string; bidderId: string }) => `v1/brands/${brandId}/bidders/${bidderId}`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    bidderId: z.string().uuid(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().email().optional(),
    phoneNumber: z.string().optional(),
    loyaltyNumber: z.string().optional(),
  }),
  responseSchema: BidderSchema,
});

export const GetAuctionStatsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
  }) => `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/stats`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    auctionId: z.string().uuid(),
  }),
  responseSchema: z.object({
    auction: AuctionApiResponseSchema,
    bidsCount: z.number().int().min(0),
    bidderCount: z.number().int().min(0),
  }),
});

export const GetAuctionBidderEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    auctionId,
    bidderId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    auctionId: string;
    bidderId: string;
  }) =>
    `v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/auctions/${auctionId}/bidder/${bidderId}`,
  requestSchema: z.object({
    brandId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    auctionId: z.string().uuid(),
    bidderId: z.string().uuid(),
  }),
  responseSchema: BidderSchema,
});
